import React from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormLabel, Grid,
  MenuItem, Radio, RadioGroup, TextField,
} from '@mui/material';
import { employmentService, positionService } from '../../services/api';
import { useToast } from '../Common/ToastProvider';
import { ANNEX_TYPES, apiErrorText, calcSalary, fmtMoney } from './salaryFormat';

// S3 — add or edit an annex. The salary is entered exactly as written in the annex (amount,
// currency, gross/net); the other value is only previewed here and calculated by the server.
const EMPTY = {
  annexNumber: '', type: 'SalaryChange', signedDate: '', effectiveDate: '', newSalary: '',
  salaryCurrency: 'EUR', salaryBasis: 'Gross', newPositionId: '', status: 'Draft', documentPath: '', description: '',
};

export default function AnnexDialog({ open, contractId, annex, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = React.useState(EMPTY);
  const [positions, setPositions] = React.useState([]);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setError('');
    setForm(annex ? {
      annexNumber: annex.annexNumber || '', type: annex.type, signedDate: (annex.signedDate || '').substring(0, 10),
      effectiveDate: (annex.effectiveDate || '').substring(0, 10), newSalary: annex.salary?.amount ?? '',
      salaryCurrency: annex.salary?.currency || 'EUR', salaryBasis: annex.salary?.basis || 'Gross',
      newPositionId: annex.newPositionId || '', status: annex.status, documentPath: annex.documentPath || '',
      description: annex.description || '',
    } : EMPTY);
    positionService.getAll().then((p) => setPositions(Array.isArray(p) ? p : [])).catch(() => setPositions([]));
  }, [open, annex]);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const year = form.effectiveDate ? Number(form.effectiveDate.substring(0, 4)) : new Date().getFullYear();
  const calc = calcSalary(form.newSalary, form.salaryCurrency, form.salaryBasis, year);
  const needsSalary = form.type === 'SalaryChange';
  const valid = form.effectiveDate && (!needsSalary || Number(form.newSalary) > 0) && (form.status !== 'Signed' || form.signedDate);

  const save = async () => {
    setSaving(true);
    setError('');
    const body = {
      annexNumber: form.annexNumber || null, type: form.type, signedDate: form.signedDate || null,
      effectiveDate: form.effectiveDate, newSalary: form.newSalary === '' ? null : Number(form.newSalary),
      salaryCurrency: form.newSalary === '' ? null : form.salaryCurrency,
      salaryBasis: form.newSalary === '' ? null : form.salaryBasis,
      newPositionId: form.newPositionId === '' ? null : Number(form.newPositionId), status: form.status,
      documentPath: form.documentPath || null, description: form.description || null,
    };
    try {
      if (annex) await employmentService.updateAnnex(annex.id, body);
      else await employmentService.addAnnex(contractId, body);
      toast(annex ? 'Annex saved' : 'Annex added', { variant: 'success' });
      onSaved?.();
    } catch (err) {
      setError(apiErrorText(err, 'Could not save the annex.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{annex ? 'Edit annex' : 'Add annex'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={6}><TextField id="annex-number" fullWidth label="Annex number" value={form.annexNumber} onChange={set('annexNumber')} /></Grid>
          <Grid item xs={6}>
            <TextField id="annex-type" select fullWidth label="Type" value={form.type} onChange={set('type')}>
              {ANNEX_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}><TextField id="annex-signed" fullWidth type="date" label="Signed on" InputLabelProps={{ shrink: true }} value={form.signedDate} onChange={set('signedDate')} /></Grid>
          <Grid item xs={6}><TextField id="annex-effective" fullWidth required type="date" label="Effective from" InputLabelProps={{ shrink: true }} value={form.effectiveDate} onChange={set('effectiveDate')} /></Grid>
          <Grid item xs={6}><TextField id="annex-salary" fullWidth required={needsSalary} type="number" label="New salary" inputProps={{ step: '0.01', min: 0 }} value={form.newSalary} onChange={set('newSalary')} /></Grid>
          <Grid item xs={6}>
            <TextField id="annex-currency" select fullWidth label="Currency" value={form.salaryCurrency} onChange={set('salaryCurrency')}>
              <MenuItem value="EUR">EUR</MenuItem><MenuItem value="BGN">BGN</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormLabel id="annex-basis-label">The amount in the document is</FormLabel>
            <RadioGroup row aria-labelledby="annex-basis-label" value={form.salaryBasis} onChange={set('salaryBasis')}>
              <FormControlLabel value="Gross" control={<Radio size="small" />} label="Gross" />
              <FormControlLabel value="Net" control={<Radio size="small" />} label="Net" />
            </RadioGroup>
          </Grid>
          {calc && (
            <Grid item xs={12}>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, fontSize: 14 }}>
                {form.salaryBasis === 'Net'
                  ? <>Calculated gross: <b>{fmtMoney(calc.gross, form.salaryCurrency)}</b></>
                  : <>Calculated net: <b>{fmtMoney(calc.net, form.salaryCurrency)}</b></>}
                {' '}· employee contributions {fmtMoney(calc.contributions)} · income tax {fmtMoney(calc.tax)} (rates {calc.ratesYear})
              </Box>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField id="annex-position" select fullWidth label="New position (optional)" value={form.newPositionId} onChange={set('newPositionId')}>
              <MenuItem value="">— no change —</MenuItem>
              {positions.map((p) => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <FormLabel id="annex-status-label">Status</FormLabel>
            <RadioGroup row aria-labelledby="annex-status-label" value={form.status} onChange={set('status')}>
              <FormControlLabel value="Draft" control={<Radio size="small" />} label="Draft" />
              <FormControlLabel value="Signed" control={<Radio size="small" />} label="Signed" />
            </RadioGroup>
          </Grid>
          <Grid item xs={12}><TextField id="annex-path" fullWidth label="Document in dossier (path)" value={form.documentPath} onChange={set('documentPath')} /></Grid>
          <Grid item xs={12}><TextField id="annex-note" fullWidth multiline minRows={2} label="Note" value={form.description} onChange={set('description')} /></Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2 }}>
          A draft does not change the contracted salary. When it is marked signed, it becomes the salary from its effective date.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save}>{annex ? 'Save annex' : 'Add annex'}</Button>
      </DialogActions>
    </Dialog>
  );
}
